/* Rhody Reviews api/v1 -- Google Business Profile image LIBRARY (2026-08-07).

   The per-client photo library that backs Google Posts. A composer uploads one
   image here; the returned public `url` is then attached to a post via
   design.photo_urls, which the publish worker hands to Google as sourceUrl.

   Wired to engine/api/v1/gbp_images.py:
     GET    /api/v1/gbp/images               list the library
     POST   /api/v1/gbp/images               upload one image (JSON image_base64)
     DELETE /api/v1/gbp/images/<id>          remove one image
     GET    /api/v1/gbp/images/requirements  Google's rules, so we can state
                                             formats + size BEFORE a failed pick

   UPLOAD IDIOM: base64 data URL through the shared JSON request() path, exactly
   like account.setBusinessPhoto. The backend _read_upload_bytes accepts
   {"image_base64": "data:image/jpeg;base64,..."} and strips the prefix itself,
   so there is one upload code path across the whole app and no FormData.

   ERROR ENVELOPE: every failure is {error, code, message, detail}. `message`
   (mirrored into detail) is a complete, plain-English sentence written for an
   owner on a phone -- render it DIRECTLY, never a code. request() surfaces it as
   ApiError.detail and the full body as ApiError.body; read gbpImageMessage()
   below to get the sentence out of either shape. */
import { ApiError, request } from './_http';

export interface GbpImage {
  id: string;
  /* Public storage URL. Drop straight into <img src>; also what rides in
     design.photo_urls when the image is attached to a post. */
  url: string;
  label: string;
  width: number | null;
  height: number | null;
  size_kb: number;
  permanent: boolean;
  used_count: number;
  last_used_at: string | null;
  published_at: string | null;
  deletes_after: string | null;
  uploaded_at: string | null;
}

export interface GbpImagesResponse {
  client_id: string;
  count: number;
  unused: number;
  rotating: number;
  permanent: number;
  rotating_capacity: number;
  permanent_capacity: number;
  images: GbpImage[];
}

export interface GbpImageUploadResult {
  client_id: string;
  image: GbpImage;
}

/* GET /gbp/images/requirements -- Google's photo rules + our policy, shaped so
   the composer can state the limits up front instead of after a rejected pick. */
export interface GbpImageRequirements {
  formats: string[];            // ['JPG', 'PNG'] -- for display
  accepted_formats: string[];   // ['jpeg', 'png'] -- magic-byte names
  min_short_edge_px: number;
  recommended_px: number;
  max_upload_mb: number;
  typical_stored_kb: number;
  rotating_capacity: number;
  permanent_capacity: number;
  grace_days: number;
  note: string;
  permanent_note: string;
}

export interface GbpImageUploadOpts {
  label?: string;
  /* Composer uploads default to the ROTATING pool (false). Permanent is for
     logos/headshots managed elsewhere. */
  permanent?: boolean;
}

export const gbpImagesApi = {
  listImages(signal?: AbortSignal): Promise<GbpImagesResponse> {
    return request<GbpImagesResponse>('/api/v1/gbp/images', { signal });
  },
  getImageRequirements(signal?: AbortSignal): Promise<GbpImageRequirements> {
    return request<GbpImageRequirements>('/api/v1/gbp/images/requirements', {
      signal,
    });
  },
  uploadImage(
    imageDataUrl: string,
    opts: GbpImageUploadOpts = {},
  ): Promise<GbpImageUploadResult> {
    return request<GbpImageUploadResult>('/api/v1/gbp/images', {
      method: 'POST',
      body: {
        image_base64: imageDataUrl,
        label: opts.label,
        permanent: opts.permanent ?? false,
      },
    });
  },
  deleteImage(id: string): Promise<{ client_id: string; deleted: string }> {
    return request<{ client_id: string; deleted: string }>(
      `/api/v1/gbp/images/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
    );
  },
};

/* Pull the owner-ready sentence out of a failed upload. The images endpoints
   put their plain-English copy in the `message` field (mirrored to `detail`),
   which request() exposes as ApiError.detail and inside ApiError.body. We NEVER
   keep our own code-to-copy table (the backend docstring is explicit: it would
   drift out of sync with the rules), so we just render whatever the server
   wrote. `fallback` covers a network error where no body came back. */
export function gbpImageMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const fromBody =
      err.body && typeof err.body.message === 'string' ? err.body.message : '';
    return fromBody || err.detail || fallback;
  }
  return fallback;
}
